import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, IconButton, Grid, Paper, Typography, useTheme, Chip, Skeleton, Divider, Stepper, Step } from '@mui/material';
import { useParams } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getReservationFailed, getReservationSuccess } from '~/actions';
import StandardPage from '~/layouts/StandardPage';
import { Box } from '@mui/system';
import { BOOKING_CONTEXT, STATUS } from '~/literals';
import { FlightReservation, Leg, PassengerLegBaggage, PassengerType, RootState } from '~/types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Done, EmailRounded } from '@mui/icons-material';
import { getFriendlyPassengerType, getPassengerLegBaggage } from '~/modules/helpers';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TripDestinationAndArrival from '~/components/TripDestinationAndArrival';
import FlightCardItemDetailsCollapsible from '~/components/FlightCardItemDetailsCollapsible';
import FlightBookingPriceSummary from '~/components/FlightBookingPriceSummary';
import { Flight } from '@mui/icons-material';
import useMediaQuery from '@mui/material/useMediaQuery';

function ManageTripPage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { id } = useParams();
  const isTab = useMediaQuery(theme.breakpoints.down('md'));


  const status = useSelector<RootState, string>(({ flightBooking }) => flightBooking.pageStatus);
  const flightReservations = useSelector<RootState, FlightReservation[]>(({ flightBooking }) => flightBooking.userReservations);
  const errorMessage = useSelector<RootState, string | undefined>(({ flightBooking }) => flightBooking?.errorMessage);
  const flightReservationByID: FlightReservation[] = flightReservations.filter(res => res.reservationId == id);
  const reservationDetails = useSelector<RootState, FlightReservation | null>(({ flightBooking }) => flightBooking.reservation);

  useEffect(() => {
    if (flightReservationByID.length) {
      dispatch(getReservationSuccess(flightReservationByID[0]));
    } else {
      dispatch(getReservationFailed({
        errorMessage: `Reservation not found.`,
      }));
    }
  }, [id]);

  let reservationInfo;
  let bannerContent;
  let displayError;
  let passengerLegBaggage: PassengerLegBaggage[] = [];

  if (reservationDetails?.bookingDetails?.offers[0].passengerPricing) {
    passengerLegBaggage = getPassengerLegBaggage(reservationDetails?.bookingDetails?.offers[0].passengerPricing, reservationDetails?.bookingDetails?.passengers || []);
  }

  if (reservationDetails && status === STATUS.SUCCESS && reservationDetails.reservationId === id) {
    const tripOutboundLegs: Leg[] = reservationDetails.bookingDetails.offers.length ?
      reservationDetails.bookingDetails.offers[0].itineraries.outbound.legs : [];


    const trip = <TripDestinationAndArrival
      departure={tripOutboundLegs[0].departure}
      arrival={tripOutboundLegs[tripOutboundLegs.length - 1].arrival}
      flightType={reservationDetails.bookingDetails.offers[0].flightType}
    />

    const outboundLegs = reservationDetails.bookingDetails.offers[0].itineraries.outbound.legs;
    if (outboundLegs && outboundLegs.length) {
      const fromCity = outboundLegs[0].departure.city.friendlyName;
      let toCity = outboundLegs[outboundLegs.length - 1].arrival.city.friendlyName;

      bannerContent = (
        <Box sx={{ color: '#fff', textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <Stepper
            sx={{
              width: {
                xs: '100%',
                sm: '60%',
                margin: '0 auto',
              },
            }}
          >
            <Step><Typography variant="h4">{fromCity}</Typography></Step>
            <Step><Flight sx={{ fontSize: 20, transform: 'rotate(45deg)', mt: 0.5 }} /></Step>
            <Step><Typography variant="h4">{toCity}</Typography></Step>
          </Stepper>
        </Box>
      );
    }

    reservationInfo = (
      <Box>
        <Grid container>
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ padding: 2, borderRadius: 5 }}>
              <Grid container spacing={5}>
                <Grid item xs={2} lg={1}>
                  <CheckCircleIcon
                    style={{
                      color: theme.palette.success.main,
                      fontSize: isTab ? 40 : 70
                    }}
                  />
                </Grid>
                <Grid item xs={10} lg={7}>
                  <Typography variant='h5' style={{ color: theme.palette.success.main }}>
                    {
                      reservationDetails.bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY ?
                        'Your itinerary reservation.'
                        :
                        'Your flight booking.'
                    }
                  </Typography>
                  <Typography variant="subtitle1">
                    Your Tripenger reference code is: <strong>{reservationDetails.localPNRCode}</strong>.
                    {reservationDetails.bookingContext !== BOOKING_CONTEXT.FLIGHT_ITINERARY && <span> Please note the airline reference code below for seat selection and online check-in, if available.</span>}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} marginTop={5} >
                <Divider />
              </Grid>
              <Grid item xs={12} marginTop={5} >
                {trip}
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Typography variant='h5' marginBottom={3} marginTop={5} >Trip Details</Typography>
            <>
              <Paper elevation={1} sx={{ padding: 2, borderRadius: 5 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  width="100%"
                  boxSizing="border-box"
                  justifyContent="space-between"
                  pl={2}
                  pr={2}
                >
                  <Chip label="Confirmed" color="success" size="small" icon={<Done />} />
                  {
                    reservationDetails.PNRRecords.length > 0 &&
                    <Typography variant='h6'>
                      <Tooltip title="You can use this reference for selecting your seat and checking in online on the airline's website.">
                        <IconButton size="small" sx={{ verticalAlign: 'middle', mr: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <small>Airline booking {reservationDetails.PNRRecords.length > 1 ? 'references' : 'reference'}:</small> {reservationDetails.PNRRecords.map(item => item.PNR).join(', ')}
                    </Typography>
                  }
                </Box>
                <FlightCardItemDetailsCollapsible
                  offer={reservationDetails.bookingDetails.offers[0]}
                  passengerPricing={reservationDetails.bookingDetails.offers[0].passengerPricing}
                  passengerLegBaggage={passengerLegBaggage}
                />
              </Paper>
            </>
          </Grid>

          <Grid item xs={12} marginTop={5}>
            <Grid container spacing={5}>
              <Grid item xs={12} lg={6} marginTop={5}>
                <Typography variant='h5' marginBottom={3}>Passenger Details</Typography>
                <Paper elevation={1} sx={{ padding: 2, borderRadius: 5 }}>
                  {
                    reservationDetails.bookingDetails.passengers.map(pax => (
                      <Typography variant='subtitle1' key={pax.id}>
                        {pax.firstName} {pax.lastName} ({getFriendlyPassengerType(pax.passengerType as PassengerType)})
                      </Typography>
                    ))
                  }
                </Paper>
              </Grid>
              <Grid item xs={12} lg={6} marginTop={5}>
                <Typography variant='h5' marginBottom={3}>Contact Details</Typography>
                <Paper elevation={1} sx={{ padding: 2, borderRadius: 5 }}>
                  {
                    reservationDetails.bookingDetails.tripContactPersons.map(person => (
                      <React.Fragment key={person.id}>
                        <Typography variant='body1'>
                          {person.firstName} {person.lastName}
                        </Typography>
                        <Box display='flex' alignItems='center'>
                          <EmailRounded />
                          <Typography variant='body1' ml={1}>
                            {person.contactDetails?.email}
                          </Typography>
                        </Box>
                      </React.Fragment>
                    ))
                  }

                </Paper>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} marginTop={5}>
            <Typography variant='h5' marginBottom={3}>Price Details</Typography>
            <FlightBookingPriceSummary
              passengers={reservationDetails.bookingDetails.passengers || []}
              passengerPricing={reservationDetails.bookingDetails.offers[0].passengerPricing}
              currency={reservationDetails.bookingDetails.offers[0].price.currency}
              agentFeesOnly={reservationDetails.bookingContext === BOOKING_CONTEXT.FLIGHT_ITINERARY}
              agentFees={reservationDetails.bookingDetails.agentFees}
              bookingContext={reservationDetails.bookingContext}
            />
          </Grid>

        </Grid>
      </Box>
    );
  } else if (errorMessage && status === STATUS.ERROR) {
    bannerContent = <Typography variant="h5" color="#fff">Error retrieving your reservation.</Typography>;
    displayError = (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        sx={{ p: 2 }}
      >
        <ErrorOutlineIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
        <Typography variant="h6" color="error" gutterBottom>
          Oops! Something went wrong.
        </Typography>
        <Typography variant="body1" gutterBottom>
          {errorMessage}
        </Typography>
      </Box>
    )
  }

  if (status === STATUS.RUNNING) {
    reservationInfo = (
      <Grid container spacing={5}>
        {[150, 150, 150].map((h, i) => <Grid item xs={12} key={i}><Skeleton variant="rectangular" width='100%' height={h} /></Grid>)}
      </Grid>
    );

    bannerContent = <Typography variant="h6"><Skeleton width="100%" height={20} sx={{ bgcolor: '#fff' }} /></Typography>
  }

  return (
    <StandardPage
      bannerContent={bannerContent}
    >
      {displayError}
      {reservationInfo}
    </StandardPage>
  );
}

export default ManageTripPage;
